import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("alias_hq_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem("alias_hq_token");
      localStorage.removeItem("alias_hq_user");
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export default api;

export const authApi = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  me: () => api.get("/auth/me"),
  getProfile: () => api.get("/me/profile"),
  updateProfile: (payload) => api.put("/me/profile", payload),
};

export const entitiesApi = {
  list: (includeArchived = false) => api.get(`/entities?include_archived=${includeArchived}`),
  get: (id) => api.get(`/entities/${id}`),
  create: (payload) => api.post("/entities", payload),
  update: (id, payload) => api.put(`/entities/${id}`, payload),
  archive: (id) => api.delete(`/entities/${id}`),
  reorder: (order) => api.post("/entities/reorder", { order }),
  overview: (id) => api.get(`/entities/${id}/overview`),
  summaries: () => api.get("/dashboard/entity_summaries"),
  get: (id) => api.get(`/entities/${id}`),
  setRoutingContext: (id, payload) => api.put(`/entities/${id}/routing-context`, payload),
  clearRoutingContext: (id) => api.delete(`/entities/${id}/routing-context`),
  extractRoutingContext: (id, input_text, mode) =>
    api.post(`/routing-context/extract`, { kind: "entity", target_id: id, input_text, mode }),
};

export const projectsApi = {
  list: (entityId) => api.get(`/projects${entityId ? `?entity_id=${entityId}` : ""}`),
  create: (payload) => api.post("/projects", payload),
  update: (id, payload) => api.put(`/projects/${id}`, payload),
  remove: (id) => api.delete(`/projects/${id}`),
  saveAsTemplate: (id, payload) => api.post(`/projects/${id}/save_as_template`, payload),
  get: (id) => api.get(`/projects/${id}`),
  setRoutingContext: (id, payload) => api.put(`/projects/${id}/routing-context`, payload),
  clearRoutingContext: (id) => api.delete(`/projects/${id}/routing-context`),
  extractRoutingContext: (id, input_text, mode) =>
    api.post(`/routing-context/extract`, { kind: "project", target_id: id, input_text, mode }),
  connectedContext: (id) => api.get(`/projects/${id}/connected-context`),
};

export const templatesApi = {
  list: (includeArchived = false) => api.get(`/project_templates?include_archived=${includeArchived}`),
  get: (id) => api.get(`/project_templates/${id}`),
  create: (payload) => api.post("/project_templates", payload),
  update: (id, payload) => api.put(`/project_templates/${id}`, payload),
  remove: (id) => api.delete(`/project_templates/${id}`),
  duplicate: (id) => api.post(`/project_templates/${id}/duplicate`),
  archive: (id) => api.post(`/project_templates/${id}/archive`),
  unarchive: (id) => api.post(`/project_templates/${id}/unarchive`),
  preview: (id, payload) => api.post(`/project_templates/${id}/preview`, payload),
  instantiate: (id, payload) => api.post(`/project_templates/${id}/instantiate`, payload),
};

export const digestApi = {
  getSettings: () => api.get("/digest/settings"),
  updateSettings: (payload) => api.put("/digest/settings", payload),
  preview: () => api.post("/digest/preview"),
  generate: () => api.post("/digest/generate"),
  send: (id) => api.post(`/digest/${id}/send`),
  testSend: () => api.post("/digest/test_send", {}),
  history: (limit = 12) => api.get(`/digest/history?limit=${limit}`),
  get: (id) => api.get(`/digest/${id}`),
  remove: (id) => api.delete(`/digest/${id}`),
};

export const teamApi = {
  listUsers: () => api.get("/team/users"),
  invite: (payload) => api.post("/team/invitations", payload),
  listInvitations: () => api.get("/team/invitations"),
  revokeInvitation: (id) => api.post(`/team/invitations/${id}/revoke`),
  disableUser: (uid) => api.post(`/team/users/${uid}/disable`),
  enableUser: (uid) => api.post(`/team/users/${uid}/enable`),
  deleteUser: (uid) => api.delete(`/team/users/${uid}`),
  listUserTasks: (uid) => api.get(`/team/users/${uid}/tasks`),
  getUser: (uid) => api.get(`/team/users/${uid}`),
  listProjectMembers: (project_id) => api.get(`/projects/${project_id}/members`),
  listMemberships: (params = {}) => api.get(`/team/memberships?${new URLSearchParams(params).toString()}`),
  addMembership: (payload) => api.post("/team/memberships", payload),
  updateMembership: (mid, payload) => api.put(`/team/memberships/${mid}`, payload),
  removeMembership: (mid) => api.delete(`/team/memberships/${mid}`),
  myPermissions: () => api.get("/me/permissions"),
  acceptInvite: (payload) => api.post("/auth/accept_invite", payload),
};

export const authResetApi = {
  forgot: (email) => api.post("/auth/forgot-password", { email }),
  reset: (token, new_password) => api.post("/auth/reset-password", { token, new_password }),
};

export const rightNowPresetsApi = {
  get: () => api.get("/me/rightnow_presets"),
  save: (payload) => api.put("/me/rightnow_presets", payload),
};

export const auditApi = {
  list: (params) => api.get("/audit", { params }),
  get: (id) => api.get(`/audit/${id}`),
  actors: () => api.get("/audit/actors"),
  // Fetch the CSV through authenticated axios so we never have to smuggle
  // a JWT via query string. The Blob is materialised in the browser and
  // triggered as a download without ever leaving the SPA.
  exportCsv: async (params) => {
    const r = await api.get("/audit/export.csv", { params, responseType: "blob" });
    return r.data;
  },
};

export const tasksApi = {
  list: (params = {}) => api.get("/tasks", { params }),
  get: (id) => api.get(`/tasks/${id}`),
  create: (payload) => api.post("/tasks", payload),
  update: (id, payload) => api.put(`/tasks/${id}`, payload),
  remove: (id) => api.delete(`/tasks/${id}`),
  quickParse: (prompt, timezone, default_entity_id) =>
    api.post("/tasks/quick_parse", { prompt, timezone, default_entity_id }),
  // --- Task Workspace writes (consolidated-build §2) ---
  addNote: (id, body) => api.post(`/tasks/${id}/notes`, { body }),
  deleteNote: (id, nid) => api.delete(`/tasks/${id}/notes/${nid}`),
  linkContact: (id, cid) => api.post(`/tasks/${id}/contacts`, { contact_id: cid }),
  unlinkContact: (id, cid) => api.delete(`/tasks/${id}/contacts/${cid}`),
  linkOrganization: (id, oid) => api.post(`/tasks/${id}/organizations`, { organization_id: oid }),
  unlinkOrganization: (id, oid) => api.delete(`/tasks/${id}/organizations/${oid}`),
  addReference: (id, url, label) => api.post(`/tasks/${id}/references`, { url, label }),
  deleteReference: (id, rid) => api.delete(`/tasks/${id}/references/${rid}`),
};

export const contactsApi = {
  list: (params = {}) => api.get("/contacts", { params }),
  get: (id) => api.get(`/contacts/${id}`),
  create: (payload) => api.post("/contacts", payload),
  update: (id, payload) => api.put(`/contacts/${id}`, payload),
  remove: (id) => api.delete(`/contacts/${id}`),
  addProjectLink: (id, project_id, relationship) =>
    api.post(`/contacts/${id}/project-links`, { project_id, relationship }),
  removeProjectLink: (id, project_id) =>
    api.delete(`/contacts/${id}/project-links/${project_id}`),
  listForProject: (project_id) => api.get(`/projects/${project_id}/contacts`),
};

export const calendarApi = {
  list: (params = {}) => api.get("/calendar", { params }),
  create: (payload) => api.post("/calendar", payload),
  update: (id, payload) => api.put(`/calendar/${id}`, payload),
  remove: (id) => api.delete(`/calendar/${id}`),
};

export const expendituresApi = {
  list: (params = {}) => api.get("/expenditures", { params }),
  create: (payload) => api.post("/expenditures", payload),
  update: (id, payload) => api.put(`/expenditures/${id}`, payload),
  remove: (id) => api.delete(`/expenditures/${id}`),
  budgetSummary: (entityId) => api.get(`/entities/${entityId}/budget_summary`),
};

export const recurringApi = {
  list: (entityId) => api.get(`/recurring${entityId ? `?entity_id=${entityId}` : ""}`),
  upcoming: (entityId, days = 60) => api.get(`/recurring/upcoming?days=${days}${entityId ? `&entity_id=${entityId}` : ""}`),
  create: (payload) => api.post("/recurring", payload),
  update: (id, payload) => api.put(`/recurring/${id}`, payload),
  pause: (id) => api.post(`/recurring/${id}/pause`),
  resume: (id) => api.post(`/recurring/${id}/resume`),
  end: (id) => api.post(`/recurring/${id}/end`),
  remove: (id) => api.delete(`/recurring/${id}`),
};

export const notesApi = {
  list: (params = {}) => api.get("/notes", { params }),
  create: (payload) => api.post("/notes", payload),
  update: (id, payload) => api.put(`/notes/${id}`, payload),
  remove: (id) => api.delete(`/notes/${id}`),
  resolve: (id, resolved = true) => api.post(`/notes/${id}/resolve`, { resolved }),
};

export const emailApi = {
  send: (payload) => api.post("/email/send", payload),
  templates: () => api.get("/email/templates"),
  createTemplate: (payload) => api.post("/email/templates", payload),
  deleteTemplate: (id) => api.delete(`/email/templates/${id}`),
};

export const filesApi = {
  upload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  downloadUrl: (id) => {
    const token = localStorage.getItem("alias_hq_token");
    return `${API}/files/${id}/download?auth=${encodeURIComponent(token || "")}`;
  },
  remove: (id) => api.delete(`/files/${id}`),
};

export const dashboardApi = {
  attention: () => api.get("/dashboard/attention"),
};

export const searchApi = {
  global: (q) => api.get(`/search?q=${encodeURIComponent(q)}`),
};

export const aiApi = {
  chat: (message, session_id) => api.post("/ai/chat", { message, session_id }),
  history: (session_id) => api.get(`/ai/history/${session_id}`),
  sessions: () => api.get("/ai/sessions"),
};

export const intakeApi = {
  analyze: (payload) => api.post("/intake/analyze", payload),
  list: (params = {}) => api.get("/intake", { params }),
  get: (id) => api.get(`/intake/${id}`),
  patch: (id, payload) => api.patch(`/intake/${id}`, payload),
  confirm: (id, confirmed) => api.post(`/intake/${id}/confirm`, { confirmed }),
  addRelationship: (id, payload) => api.post(`/intake/${id}/add_relationship`, payload),
};

export const signalsApi = {
  list: (params = {}) => api.get("/cross-entity-signals", { params }),
  get: (id) => api.get(`/cross-entity-signals/${id}`),
  review: (id) => api.post(`/cross-entity-signals/${id}/review`),
  dismiss: (id, reason) => api.post(`/cross-entity-signals/${id}/dismiss`, { reason }),
  route: (id) => api.post(`/cross-entity-signals/${id}/route`),
  count: () => api.get("/cross-entity-signals/_summary/count"),
};

export const organizationsApi = {
  list: (params = {}) => api.get("/organizations", { params }),
  get: (id) => api.get(`/organizations/${id}`),
  create: (payload) => api.post("/organizations", payload),
  update: (id, payload) => api.put(`/organizations/${id}`, payload),
  addRelationship: (id, payload) => api.post(`/organizations/${id}/relationships`, payload),
  updateRelationship: (id, rid, payload) => api.patch(`/organizations/${id}/relationships/${rid}`, payload),
  linkContact: (id, cid) => api.post(`/organizations/${id}/contacts/${cid}`),
  unlinkContact: (id, cid) => api.delete(`/organizations/${id}/contacts/${cid}`),
  convertNote: (noteId, payload) => api.post(`/notes/${noteId}/convert-to-organization`, payload),
  createNote: (id, payload) => api.post(`/organizations/${id}/notes`, payload),
  updateNote: (id, nid, payload) => api.patch(`/organizations/${id}/notes/${nid}`, payload),
  deleteNote: (id, nid) => api.delete(`/organizations/${id}/notes/${nid}`),
};

export const quickApi = {
  parse: ({ prompt, timezone, section_hint, default_entity_id, default_project_id, default_contact_id }) =>
    api.post("/quick/parse", { prompt, timezone, section_hint, default_entity_id, default_project_id, default_contact_id }),
  commit: (records) => api.post("/quick/commit", { records }),
};
